import { ProDescriptions } from "@ant-design/pro-components";
import { Link, useLocation, useParams } from "react-router-dom";
import { BasicTable, HView, CustomDetailPage, Status } from "@/components";
import { useTranslation } from "react-i18next";
import { useLiftRbes } from "@/hooks";
import { useMemo } from "react";
import {
  TABLEPAI,
  PageEnum,
  LMD_LIFT_TABLE_HEADER,
  LMD_ALARM_TABLE_HEADER,
  COL1,
  COL2,
} from "@/enums/pageEnum";
import { LiftColumns } from "@/utils/commonConfig";
import AlarmComponet from "./AlarmComponet";
import { getTimeToString } from "@/utils/toolt";

export default function LmdDetailPage() {
  const { t } = useTranslation();
  const { state } = useLocation();
  const { id } = useParams();
  const { rebTempers } = useLiftRbes();
  const INFO = "#INFO";
  const columns = useMemo(() => {
    return [
      { title: t("ward"), dataIndex: "ward", ...COL1 },
      { title: t("location"), dataIndex: "location", ...COL2 },
      { title: t("lmdMrlocation"), dataIndex: "lmdMrlocation", ...COL1 },
      { title: t("address"), dataIndex: "address" },
      { title: t("lmdInstallDate"), dataIndex: "lmdInstallDate" },
      { title: t("hardware"), dataIndex: "hardware" },
      { title: t("software"), dataIndex: "softwareVersion", span: 3 },
      { title: `${t("dataLine")} #`, dataIndex: "dataLine" },
      { title: t("arsId"), dataIndex: "arsId" },
      { title: t("altBldg"), dataIndex: "altBldg" },
      { title: t("baudRate"), dataIndex: "baudRate", span: 3 },
      { title: t("maintenanceCompany"), dataIndex: "maintenanceCompany" },
      { title: t("brand"), dataIndex: "brand" },
      { title: t("contactTerm"), dataIndex: "contactTerm" },
      { title: t("propertyManager"), dataIndex: "propertyManager" },
      { title: t("phoneNumber"), dataIndex: "phoneNumber", span: 2 },
      { title: t("simIp"), dataIndex: "simIp" },
      { title: t("lmdIp"), dataIndex: "lmdIp" },
      { title: t("gprsIsp"), dataIndex: "gprsIsp" },
      { title: t("modemVendor"), dataIndex: "modemVendor", span: 3 },
      { title: t("vpTunIp"), dataIndex: "vpTunIp" },
      { title: t("lmdTunIp"), dataIndex: "lmdTunIp", span: 2 },
      { title: t("rowIdA"), dataIndex: "rowIdA" },
      { title: t("rowIdB"), dataIndex: "rowIdB", span: 2 },
      { title: t("mlaChannel"), dataIndex: "mlaChannel" },
      { title: t("lineScpNbr"), dataIndex: "lineScpNbr", span: 2 },
      { title: t("lmdStatus1"), dataIndex: "lmdStatus1" },
      { title: t("lmdStatus2"), dataIndex: "lmdStatus2" },
      { title: t("lmdStatus3"), dataIndex: "lmdStatus3" },
      { title: t("lmdSwitchAddress"), dataIndex: "lmdSwitchAddress", span: 3 },
      { title: t("dvrTerm"), dataIndex: "dvrTerm" },
      { title: t("dvrType"), dataIndex: "dvrType" },
      { title: t("dvrPAddress"), dataIndex: "dvrPAddress" },
      { title: t("subnetGateway1"), dataIndex: "subnetGateway1", span: 3 },
      { title: t("subnetGateway5"), dataIndex: "subnetGateway5", span: 3 },
      { title: t("remarks"), dataIndex: "remarks", span: 3 },
      { title: t("RST"), dataIndex: "rst", span: 3 },
    ];
  }, [t]);
  const tabList = useMemo(() => {
    return [
      { tab: `LMD ${t("info")}`, key: INFO },
      { tab: t("alarm"), key: "#ALARM" },
      { tab: t("lift"), key: "#LIFT" },
    ];
  }, [t]);
  return (
    <CustomDetailPage
      tabList={tabList}
      url="lmd"
      funcChildren={(key: string, lmdData: any) => {
        if (key === INFO) {
          return (
            <div className="bg-[#fff] dark:bg-[#141414] min-h-[calc(100vh-297px)] p-4">
              <ProDescriptions
                bordered
                dataSource={lmdData?.attribute}
                columns={columns}
              />
            </div>
          );
        } else if (key === "#ALARM") {
          return (
            <AlarmComponet
              editTableHeaderUrl={LMD_ALARM_TABLE_HEADER}
              isLmdlink={false}
              data={rebTempers}
              rbeApiCode={`${TABLEPAI.RBE_CODE}?view=LMD`}
            />
          );
        }
        return (
          <BasicTable
            isShowCheckbox={false}
            tableColsRender={[...LiftColumns(false, true)]}
            tableName="lift"
            editTableHeaderUrl={LMD_LIFT_TABLE_HEADER}
            headerTitle={() => t("liftList")}
            className="min-h-[calc(100vh-297px)]"
            exportUrl={`/api/lmd/${id}/lift/export`}
            requestUrl={`/api/lmd/${id}/lift/page`}
          />
        );
      }}
      headerCongif={(_: string, lmdData: any) => ({
        title: (
          <>
            <span className="text-[20px] font-medium mr-4">
              {lmdData?.name}
            </span>
            <Status status={lmdData?.onlineFormat} />
          </>
        ),
        children: (
          <div className="mb-4">
            <div className="mb-4">
              Last update at: {getTimeToString(lmdData?.lastOnlineTs)}
            </div>
            <div>
              <span className="mr-10">
                {t("building")}:{" "}
                <HView
                  name={lmdData?.building?.name}
                  path={`${PageEnum.BUILDING}/detail/${lmdData?.building?.id}`}
                  state={state}
                  isNewWindow
                />
              </span>
              <span className="mr-10">
                {t("address")}: {lmdData?.attribute?.address}
              </span>
              <span className="mr-10">
                {t("brand")}: {lmdData?.attribute?.brand}
              </span>
              <span className="mr-10">
                {t("hardware")}: {lmdData?.attribute?.hardware}
              </span>
              <span>
                {t("software")}: {lmdData?.attribute?.softwareVersion}
              </span>
            </div>
          </div>
        ),
        breadcrumb: {
          items: [
            { title: <Link to={PageEnum.LMD}>{t("lmd")}</Link> },
            { title: t("detail") },
          ],
        },
      })}
    />
  );
}
